import { VisuallyHidden } from "@reach/visually-hidden";
import classNames from "classnames";
import { TextInput } from "components/Input/Input";
import type { DRFPaginatedQuery } from "interfaces";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import CaretIcon from "../../public/svg/caret.svg";

import styles from "./page-nav-bar.module.scss";

type PaginationProps = Pick<
    DRFPaginatedQuery<unknown>,
    "next" | "previous" | "count"
>;

type Props = PaginationProps & {
    padBottom: boolean;
};

function usePageNumber({ next, previous, count }: PaginationProps) {
    const notApplicable = [1, 1, undefined] as const;
    const url = previous ?? next;
    // If there is neither next nor previous there's only 1 page
    if (!url) return notApplicable;
    const params = new URL(url).searchParams;
    const limit = parseInt(params.get("limit") ?? "0");
    if (!limit) return notApplicable;
    const totalPages = Math.ceil(count / limit);

    const calculateTargetOffset = (targetPage: number) => {
        if (targetPage < 1 || targetPage > totalPages)
            throw new Error("Oi! What you trying to pull");
        return limit * (targetPage - 1);
    };
    // if there is no "previous" we're on the first page
    if (!previous) return [1, totalPages, calculateTargetOffset] as const;
    // since previous exists, this is the offset to get to the previous page
    const offset = parseInt(params.get("offset") ?? "0");
    // If there's no offset then the previous page is the first page
    if (!offset) return [2, totalPages, calculateTargetOffset] as const;
    const currentPage = Math.ceil(offset / limit) + 2;

    return [currentPage, totalPages, calculateTargetOffset] as const;
}

export function PageNavBar({ next, previous, count, padBottom }: Props) {
    const [currentPage, totalPages, calculateTargetOffset] = usePageNumber({
        next,
        previous,
        count,
    });
    const router = useRouter();

    const [targetPage, setTargetPage] = useState(currentPage);

    useEffect(() => {
        setTargetPage(currentPage);
    }, [currentPage]);

    const getViewLessParams = () => {
        const query = { ...router.query };
        // Looks nicer than `&offset=&limit=`
        delete query.offset;
        delete query.limit;
        return query;
    };

    return (
        <div
            className={classNames({
                [styles.container]: true,
                [styles.padBottom]: padBottom,
            })}
        >
            <div>
                <GridPageButton
                    to={previous}
                    className={`${styles.previous} al-grid-page__prev`}
                >
                    <CaretIcon />
                    <VisuallyHidden>Go back one page</VisuallyHidden>
                </GridPageButton>
                <form
                    className={styles.jumpToPage}
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (targetPage && calculateTargetOffset) {
                            const targetOffset =
                                calculateTargetOffset(targetPage);
                            router.push({
                                query: {
                                    ...router.query,
                                    offset: targetOffset,
                                },
                            });
                        }
                    }}
                >
                    <VisuallyHidden>Jump to page</VisuallyHidden>
                    <TextInput
                        onChange={(e) =>
                            setTargetPage(parseInt(e.target.value))
                        }
                        value={targetPage}
                        type="text"
                        name="page"
                    />
                    <span className={styles.pageTotal}>of {totalPages}</span>
                    <button type="submit" hidden>
                        Submit
                    </button>
                </form>
                <GridPageButton
                    to={next}
                    className={`${styles.next} al-grid-page__next`}
                >
                    <CaretIcon />
                    <VisuallyHidden>Go forward one page</VisuallyHidden>
                </GridPageButton>
            </div>
            <div className={styles.buttonContainer}>
                {totalPages !== 1 && (
                    <Link
                        href={{
                            query: { ...router.query, offset: 0, limit: count },
                        }}
                        shallow
                    >
                        <a className={`${styles.buttonText} al-grid-page__all`}>
                            View All
                        </a>
                    </Link>
                )}
                {totalPages === 1 &&
                    !!router.query.limit &&
                    router.query.limit !== "0" && (
                        <Link href={{ query: getViewLessParams() }} shallow>
                            <a
                                className={`${styles.buttonText} al-grid-page__less`}
                            >
                                View Less
                            </a>
                        </Link>
                    )}
            </div>
        </div>
    );
}

function GridPageButton({
    to,
    onClick,
    ...props
}: { to?: string | null } & Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    "href"
>) {
    const router = useRouter();

    if (!to) return <a {...props} href={undefined} />;

    const searchParams = new URL(to).searchParams;
    const newQuery = {
        ...router.query,
        offset: searchParams.get("offset"),
    };
    return (
        <Link scroll={true} href={{ query: newQuery }} shallow>
            <a {...props} />
        </Link>
    );
}
